/**
 * The inline expansion under a task row: markdown description (plain
 * auto-sizing textarea in 1.0), native date + time inputs (fine in Firefox),
 * created/completed metadata, and permanent delete behind an inline confirm.
 */
import { useRef, useState } from "react";
import type { Recurrence, TaskItem } from "@hydra-notes/shared";
import { Button } from "@/ui/Button";
import { RecurrenceEditor } from "./RecurrenceEditor";
import s from "./TaskListView.module.css";

export function TaskDetail({
  task,
  readOnly,
  onUpdate,
  onDelete,
}: {
  task: TaskItem;
  readOnly: boolean;
  onUpdate: (fields: {
    description?: string;
    dueDate?: string | null;
    dueTime?: string | null;
    recurrence?: Recurrence | null;
  }) => void;
  onDelete: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const descRef = useRef<HTMLTextAreaElement>(null);

  const autosize = () => {
    const el = descRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`;
  };

  return (
    <div className={s.detail}>
      <textarea
        ref={descRef}
        className={s.detailDesc}
        placeholder="Details (markdown)"
        defaultValue={task.description ?? ""}
        readOnly={readOnly}
        rows={2}
        onFocus={autosize}
        onInput={autosize}
        onBlur={(e) => {
          const v = e.target.value;
          if (v !== (task.description ?? "")) onUpdate({ description: v });
        }}
      />
      <RecurrenceEditor
        rule={task.recurrence}
        readOnly={readOnly}
        onChange={(rule) => onUpdate({ recurrence: rule ?? null })}
      />
      <div className={s.detailRow}>
        <label className={s.detailLabel}>
          Due
          <input
            type="date"
            className={s.detailInput}
            value={task.dueDate ?? ""}
            disabled={readOnly}
            onChange={(e) => onUpdate({ dueDate: e.target.value || null })}
          />
        </label>
        <label className={s.detailLabel}>
          Time
          <input
            type="time"
            className={s.detailInput}
            value={task.dueTime ?? ""}
            disabled={readOnly || !task.dueDate}
            onChange={(e) => onUpdate({ dueTime: e.target.value || null })}
          />
        </label>
        <span className={s.detailMeta}>
          Created {new Date(task.createdAt).toLocaleDateString()}
          {task.doneAt ? ` · Completed ${new Date(task.doneAt).toLocaleString()}` : ""}
        </span>
        {!readOnly ? (
          confirming ? (
            <span className={s.confirmGroup}>
              <Button variant="danger" size="sm" onClick={onDelete}>
                Delete forever
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                Keep
              </Button>
            </span>
          ) : (
            <Button variant="ghost" size="sm" className={s.deleteBtn} onClick={() => setConfirming(true)}>
              Delete
            </Button>
          )
        ) : null}
      </div>
    </div>
  );
}
